import { Pencil, Eye, Paperclip, FileWarning, AlertCircle, Route } from 'lucide-react';
import type { Revenue } from '../../types/finance';
import type { UserRole } from '../../types/user';
import { Button } from '../ui/Button';
import { formatDate, formatTime } from '../../utils/trips';
import { formatearCOP } from '../../utils/expenses';
import { formatearCantidad, puedeCorregirIngreso, dentroDeVentanaEdicionIngreso, sinSoporte, cobradoDistintoDelPropuesto } from '../../utils/finance';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useAuth } from '../../context/AuthContext';

interface Props {
  revenues: Revenue[];
  role?: UserRole;
  onVerDetalle: (r: Revenue) => void;
  /** Ausente = solo lectura (el expediente del viaje). */
  onCorregir?: (r: Revenue) => void;
}

// Cantidad y Cobrado van a la derecha y Propuesto a la izquierda: cada columna
// numérica lleva su margen interior para que no se lean pegadas a la siguiente.
const GRID = '110px 1fr 1fr 120px 150px 1fr 80px 100px';
const NUMERO: React.CSSProperties = { textAlign: 'right', paddingRight: '1.5rem' };

/**
 * FE-32 — el listado de ingresos. Dos cosas lo distinguen del de gastos:
 *
 *  - la columna **Propuesto / Cobrado**: cuando difieren se marca, porque es
 *    la decisión de una persona sobre lo que la tarifa decía (ADR-030 §2);
 *  - en teléfono (≤768 px) deja de ser tabla y pasa a **tarjetas**: el DoD de
 *    FE-32 prohíbe el scroll horizontal, y una grilla de ocho columnas no cabe
 *    en 390 px por mucho que se apriete.
 */
export function RevenuesTable({ revenues, role, onVerDetalle, onCorregir }: Props) {
  const movil = useMediaQuery('(max-width: 768px)');
  // FE-35: corregir exige rol Y módulo; el usuario completo sale del contexto.
  const { user } = useAuth();
  const puedeCorregir = puedeCorregirIngreso(user);
  const unidad = (r: Revenue) => (typeof r.tariffSnapshot?.unit === 'string' ? r.tariffSnapshot.unit : undefined);

  if (movil) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
        {revenues.map(r => (
          <div key={r.id} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '0.875rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.75rem' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{formatDate(r.occurredAt)}</span>
              <strong style={{ fontSize: '16px', fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{formatearCOP(r.amount)}</strong>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
              {r.customerName}{r.tariffName ? <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}> · {r.tariffName}</span> : null}
              {r.origin === 'TRIP_START' && <MarcaDelViaje />}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.875rem', fontSize: '12px', color: 'var(--text-secondary)' }}>
              {r.quantity && <span>{formatearCantidad(r.quantity, unidad(r))}</span>}
              {r.vehiclePlate && <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.vehiclePlate}</span>}
              {cobradoDistintoDelPropuesto(r) && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#8B5CF6', fontWeight: 700 }}>
                  <AlertCircle size={12} /> propuesto {formatearCOP(r.computedAmount!)}
                </span>
              )}
              {sinSoporte(r) ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#F59E0B', fontWeight: 700 }}><FileWarning size={12} /> sin soporte</span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#10B981', fontWeight: 700 }}><Paperclip size={12} /> soporte</span>
              )}
            </div>
            <div className="expenses-table__actions" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              {onCorregir && puedeCorregir && dentroDeVentanaEdicionIngreso(r, role) && (
                <Button size="sm" variant="secondary" onClick={() => onCorregir(r)} leftIcon={<Pencil size={13} />}>Corregir</Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => onVerDetalle(r)} leftIcon={<Eye size={13} />}>Detalle</Button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: '960px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: '0.75rem', padding: '0.75rem 1rem', background: 'var(--bg-raised)', borderBottom: '1px solid var(--border)', fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            <span>Fecha</span>
            <span>Cliente · tarifa</span>
            <span>Vehículo / conductor</span>
            <span style={NUMERO}>Cantidad</span>
            <span style={NUMERO}>Cobrado</span>
            <span>Propuesto</span>
            <span>Soporte</span>
            <span style={{ textAlign: 'right' }}>Acciones</span>
          </div>
          {revenues.map(r => (
            <div key={r.id} style={{ display: 'grid', gridTemplateColumns: GRID, gap: '0.75rem', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: '12px', color: 'var(--text-secondary)' }}>
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{formatDate(r.occurredAt)}</strong>
                <span style={{ color: 'var(--text-muted)' }}>{formatTime(r.occurredAt)}</span>
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ color: 'var(--text-primary)', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.customerName}</span>
                <span style={{ color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.tariffName ?? 'sin tarifa'}{r.origin === 'TRIP_START' && <MarcaDelViaje />}</span>
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--text-primary)' }}>{r.vehiclePlate || (r.vehicleId ? 'vehículo' : '—')}</span>
                <span style={{ color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.driverName ?? (r.driverId ? 'conductor' : '—')}</span>
              </span>
              <span style={{ ...NUMERO, fontVariantNumeric: 'tabular-nums' }}>{formatearCantidad(r.quantity, unidad(r))}</span>
              <span style={{ ...NUMERO, fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{formatearCOP(r.amount)}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {r.computedAmount ? (
                  cobradoDistintoDelPropuesto(r)
                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#8B5CF6', fontWeight: 700 }} title="Se cobró un importe distinto del que la tarifa propuso; la razón está en la bitácora."><AlertCircle size={12} /> {formatearCOP(r.computedAmount)}</span>
                    : <span style={{ color: 'var(--text-muted)' }}>= propuesto</span>
                ) : <em style={{ color: 'var(--text-muted)' }}>—</em>}
              </span>
              <span>
                {sinSoporte(r)
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '11px', fontWeight: 700, color: '#F59E0B' }}><FileWarning size={13} /> Falta</span>
                  : <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '11px', fontWeight: 700, color: '#10B981' }}><Paperclip size={13} /> Sí</span>}
              </span>
              <span className="expenses-table__actions" style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                {onCorregir && puedeCorregir && (
                  <Button size="sm" variant="secondary" onClick={() => onCorregir(r)} disabled={!dentroDeVentanaEdicionIngreso(r, role)} aria-label="Corregir ingreso"
                    title={dentroDeVentanaEdicionIngreso(r, role) ? undefined : 'Pasaron más de 2 semanas desde el registro: ya no se puede corregir.'}>
                    <Pencil size={13} />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => onVerDetalle(r)} aria-label="Ver detalle del ingreso"><Eye size={13} /></Button>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * FE-34 — el ingreso que creó el core al INICIAR el viaje (ADR-032 §5). La
 * marca existe para que nadie lo registre dos veces a mano: se corrige, no se
 * duplica.
 */
export function MarcaDelViaje() {
  return (
    <span title="Lo creó el core al iniciar el viaje, con el peso que declaró el conductor. Para ajustarlo, usa «Corregir»." style={{ marginLeft: '0.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.05rem 0.45rem', borderRadius: '999px', background: 'rgba(139,92,246,0.14)', color: '#8B5CF6', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em', verticalAlign: 'middle' }}>
      <Route size={10} /> del viaje
    </span>
  );
}
